/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/sheets/QaPhoneScopeSheet.tsx
 * FILE_ID: JOBHUB-QA-PHONE-SCOPE-001
 * PURPOSE: One sheet for areas, checklists, or quote sheets. Search + optional multi-select.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { MoreHorizontal, Pencil } from 'lucide-react'
import { QaDragHandle } from '../../../../qa/QaDragHandle'
import { MobileSheet, type ContextMenuItem } from '@quotech/ui'
import { QaInlineMenuButtons } from '../../../../qa/qaInlineMenuButtons'
import { InlineRenameInput } from '../../../../qa/InlineRenameInput'
import { QA_PHONE_CONTROLS } from '../qaPhoneControlInventory'
import { QaChecklistJumpList, type QaJumpChecklist } from '../../QaChecklistJumpList'

export type ScopeRow = { id: string; name: string; group?: string }

export function QaPhoneScopeSheet({
  open,
  onClose,
  title,
  rows,
  currentId,
  selecting,
  selectedIds,
  onToggleSelectMode,
  onPick,
  onToggle,
  onAdd,
  addLabel,
  onRename,
  menuFor,
  renameId,
  onRenameStarted,
  jumpRows,
  onJump,
  currentAreaId,
  levelRows,
  onOpenLevel,
}: {
  open: boolean
  onClose: () => void
  title: string
  rows: ScopeRow[]
  currentId: string | null
  selecting: boolean
  selectedIds: ReadonlySet<string>
  onToggleSelectMode: () => void
  onPick: (id: string) => void
  onToggle: (id: string) => void
  onAdd?: () => void
  addLabel?: string
  /**
   * Optional per-row rename (pencil -> inline input). Areas and checklists
   * pass it; quote sheets do not. Everything a user typed must stay editable
   * (operator mandate, 2026-09-15); renames are audit-logged server-side.
   */
  onRename?: (id: string, name: string) => void | Promise<void>
  menuFor?: (rowId: string) => ContextMenuItem[]
  renameId?: string | null
  onRenameStarted?: () => void
  jumpRows?: readonly QaJumpChecklist[]
  onJump?: (areaId: string, checklistId: string) => void
  currentAreaId?: string | null
  levelRows?: { id: string; name: string; empty: boolean }[]
  onOpenLevel?: (id: string, point: { clientX: number; clientY: number }) => void
}) {
  const [query, setQuery] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [menuRowId, setMenuRowId] = useState<string | null>(null)
  const hold = useRef<number | null>(null)
  const suppressUntil = useRef(0)
  const currentRef = useRef<HTMLButtonElement | null>(null)
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return needle ? rows.filter((row) => row.name.toLowerCase().includes(needle)) : rows
  }, [query, rows])
  const groups = useMemo(() => {
    const map = new Map<string, ScopeRow[]>()
    for (const row of shown) {
      const key = row.group ?? ''
      const list = map.get(key) ?? []
      list.push(row)
      map.set(key, list)
    }
    return [...map.entries()]
  }, [shown])

  useEffect(() => {
    if (open) currentRef.current?.scrollIntoView({ block: 'nearest' })
  }, [open, currentId])
  useEffect(() => {
    if (!renameId) return
    setRenamingId(renameId)
    onRenameStarted?.()
  }, [renameId, onRenameStarted])

  function openRowMenu(rowId: string, _event: { clientX: number; clientY: number }) {
    if (!menuFor) return
    setMenuRowId(rowId)
  }
  function armMenu(rowId: string, point: { clientX: number; clientY: number }) {
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = window.setTimeout(() => {
      suppressUntil.current = Date.now() + 750
      openRowMenu(rowId, point)
    }, 500)
  }
  function disarmMenu() {
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = null
  }
  function armLevel(levelId: string, point: { clientX: number; clientY: number }) {
    if (!onOpenLevel) return
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = window.setTimeout(() => {
      suppressUntil.current = Date.now() + 750
      onOpenLevel(levelId, point)
    }, 500)
  }

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title={title}
      size="full"
      footer={onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          className="flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary text-sm font-semibold text-primary-foreground"
        >
          {addLabel ?? 'Add'}
        </button>
      ) : undefined}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search"
          className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-border bg-background px-3 text-base"
        />
        <button
          type="button"
          onClick={onToggleSelectMode}
          className="min-h-[44px] shrink-0 px-2 text-xs font-semibold text-primary"
        >
          {selecting ? 'Done' : 'Select'}
        </button>
      </div>
      {jumpRows && onJump ? (
        <QaChecklistJumpList
          rows={jumpRows.filter((row) => {
            const needle = query.trim().toLowerCase()
            return !needle || row.name.toLowerCase().includes(needle) || row.areaName.toLowerCase().includes(needle)
          })}
          currentAreaId={currentAreaId ?? (currentId ? jumpRows.find((row) => row.id === currentId)?.areaId ?? null : null)}
          currentChecklistId={currentId}
          onPick={onJump}
        />
      ) : <>{groups.map(([group, items]) => (
        <div key={group || 'all'}>
          {group ? (
            <div
              data-qa-phone-control={QA_PHONE_CONTROLS.floorGroup}
              data-qa-drop={`level:${levelRows?.find((level) => level.name === group)?.id ?? group}`}
              className="flex min-h-[44px] items-center px-2 data-[qa-drop-active=true]:border-t-2 data-[qa-drop-active=true]:border-primary"
              onContextMenu={onOpenLevel ? (event) => {
                event.preventDefault()
                const level = levelRows?.find((row) => row.name === group)
                if (level) onOpenLevel(level.id, event)
              } : undefined}
              onTouchStart={onOpenLevel ? (event) => {
                const level = levelRows?.find((row) => row.name === group)
                const touch = event.touches[0]
                if (level && touch) armLevel(level.id, touch)
              } : undefined}
              onTouchEnd={disarmMenu}
              onTouchMove={disarmMenu}
            >
              {levelRows?.find((level) => level.name === group) ? (
                <QaDragHandle kind="level" id={levelRows.find((level) => level.name === group)!.id} label={group} />
              ) : null}
              <span className="min-w-0 flex-1 truncate text-[11px] font-semibold uppercase text-muted-foreground">{group}</span>
              {onOpenLevel && levelRows?.find((level) => level.name === group) ? (
                <button
                  type="button"
                  aria-label={`More actions for ${group}`}
                  className="flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground"
                  onClick={(event) => {
                    const level = levelRows?.find((row) => row.name === group)
                    if (level) onOpenLevel(level.id, event)
                  }}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                </button>
              ) : null}
            </div>
          ) : null}
          {items.map((row) => {
            const current = row.id === currentId
            if (renamingId === row.id && onRename) {
              return (
                <InlineRenameInput
                  key={row.id}
                  initialValue={row.name}
                  ariaLabel={`Rename ${row.name}`}
                  className="min-h-[44px] px-4"
                  onSubmit={(value) => {
                    setRenamingId(null)
                    void onRename(row.id, value)
                  }}
                  onCancel={() => setRenamingId(null)}
                />
              )
            }
            return (
              <Fragment key={row.id}>
              <div
                data-qa-drop={`location:${row.id}`}
                className="flex min-w-0 items-center data-[qa-drop-active=true]:border-t-2 data-[qa-drop-active=true]:border-primary"
                onContextMenu={menuFor ? (event) => {
                  event.preventDefault()
                  openRowMenu(row.id, event)
                } : undefined}
                onTouchStart={menuFor ? (event) => {
                  const touch = event.touches[0]
                  if (touch) armMenu(row.id, touch)
                } : undefined}
                onTouchEnd={disarmMenu}
                onTouchMove={disarmMenu}
              >
                <QaDragHandle kind="location" id={row.id} label={row.name} />
                {/* Reused from: packages/job-hub/src/jobs/tabs/JobQaListChips.tsx (useHoldToOpenMenu click guard) */}
                <div
                  className="min-w-0 flex-1"
                  onClickCapture={(event) => {
                    if (Date.now() > suppressUntil.current) return
                    event.preventDefault()
                    event.stopPropagation()
                    suppressUntil.current = 0
                  }}
                >
                <button
                  type="button"
                  ref={current ? currentRef : undefined}
                  onClick={() => (selecting ? onToggle(row.id) : onPick(row.id))}
                  className="flex min-h-[44px] w-full min-w-0 items-center gap-2 px-4 text-left"
                >
                  {selecting ? (
                    <input type="checkbox" readOnly checked={selectedIds.has(row.id)} className="h-4 w-4" />
                  ) : current ? (
                    <span aria-hidden className="text-primary">✓</span>
                  ) : (
                    <span className="w-4" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm">{row.name}</span>
                </button>
                </div>
                {menuFor && !selecting ? (
                  <button
                    type="button"
                    onClick={(event) => openRowMenu(row.id, event)}
                    aria-label={`More actions for ${row.name}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
                {onRename && !selecting ? (
                  <button
                    type="button"
                    onClick={() => setRenamingId(row.id)}
                    aria-label={`Rename ${row.name}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>
              {menuRowId === row.id && menuFor ? (
                <QaInlineMenuButtons items={menuFor(row.id)} onDone={() => setMenuRowId(null)} />
              ) : null}
              </Fragment>
            )
          })}
        </div>
      ))}
      {levelRows?.filter((level) => level.empty && !groups.some(([name]) => name === level.name)).map((level) => (
        <div key={level.id} data-qa-drop={`level:${level.id}`} className="px-2">
          <div className="flex min-h-[44px] items-center">
            <QaDragHandle kind="level" id={level.id} label={level.name} />
            <span className="text-[11px] font-semibold uppercase text-muted-foreground">{level.name}</span>
          </div>
          <p className="px-2 pb-1 text-[11px] text-muted-foreground/70">Empty. Move locations here.</p>
        </div>
      ))}
      </>}
    </MobileSheet>
  )
}
